import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Lock, ArrowLeft, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, money, applyBrand } from "@/lib/api";
import { ScoreRing, VerdictBadge } from "@/components/ScoreRing";
import { SECTIONS } from "@/components/sectionConfig";

const Watermark = () => <div aria-hidden className="pointer-events-none fixed inset-0 z-40 grid place-items-center select-none"><span className="font-display font-extrabold text-[22vw] sm:text-[160px] tracking-[0.2em] text-white/[0.04] -rotate-[35deg]">DRAFT</span></div>;

const Locked = ({ title, teaser, invoice }) => (
  <section className="rounded-xl border border-slate-800 bg-slate-900/70 p-6" data-testid="preview-locked-section">
    <h2 className="font-display text-xl font-bold mb-3">{title}</h2>
    {teaser && <p className="text-slate-300 leading-relaxed">{teaser}</p>}
    <div className="relative mt-4 overflow-hidden rounded-lg">
      <div className="space-y-2 blur-sm select-none" aria-hidden>{[90, 75, 85, 60].map((w) => <div key={w} className="h-3 rounded bg-slate-700/70" style={{ width: `${w}%` }} />)}</div>
      <div className="absolute inset-0 grid place-items-center bg-slate-950/40">
        <span className="flex items-center gap-2 text-sm text-slate-200"><Lock className="h-4 w-4 text-amber-400" />Unlock the full study{invoice && <a href={`/pay/${invoice.token}`} className="ml-2 underline text-amber-300">Pay now</a>}</span>
      </div>
    </div>
  </section>
);

export default function PortalPreview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api.get("/portal/me").then((r) => applyBrand(r.data.brand)).catch(() => navigate("/portal/login"));
    api.get(`/portal/studies/${id}/preview`).then((r) => (r.data.unlocked ? navigate(`/portal/studies/${id}`, { replace: true }) : setD(r.data)))
      .catch((e) => setError(e.response?.status === 402 ? "This study unlocks once its invoice is paid." : "Study not found."));
  }, [id, navigate]);
  if (error) return <div className="min-h-screen grid place-items-center text-center px-4" data-testid="preview-error"><div><Lock className="h-8 w-8 text-amber-400 mx-auto mb-3" /><p className="text-slate-300 mb-4">{error}</p><Link to="/portal" className="text-emerald-400 underline">Back to portal</Link></div></div>;
  if (!d) return <div className="min-h-screen" />;
  const es = d.executive_summary || {};
  const pay = d.invoice && <Button asChild data-testid="preview-pay-btn" className="rounded-full bg-amber-400 text-amber-950 hover:bg-amber-300"><a href={`/pay/${d.invoice.token}`}>Unlock full study · {money(d.invoice.amount, d.invoice.currency)}</a></Button>;
  return (
    <div className="min-h-screen" data-testid="portal-preview-page" onContextMenu={(e) => e.preventDefault()}>
      <Watermark />
      <header className="border-b border-slate-800 bg-slate-950/80 sticky top-0 z-30 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between gap-4">
          <Link to="/portal" className="flex items-center gap-2 text-sm text-slate-400 hover:text-white" data-testid="preview-back"><ArrowLeft className="h-4 w-4" />My portal</Link>
          <span className="text-xs uppercase tracking-[0.25em] text-amber-300 font-semibold" data-testid="preview-draft-badge">Draft preview</span>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 py-10 space-y-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-center gap-6">
            <ScoreRing score={d.score} size={84} testId="preview-score" />
            <div>
              <div className="flex items-center gap-3 mb-2"><span className="text-xs uppercase tracking-wider text-slate-500">{d.input.industry || "Feasibility study"}</span><VerdictBadge verdict={d.verdict} testId="preview-verdict" /></div>
              <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="preview-title">{d.input.project_name}</h1>
              {d.input.address && <p className="flex items-center gap-1 text-sm text-cyan-300 mt-2"><MapPin className="h-4 w-4" />{d.input.address}</p>}
            </div>
          </div>
          {pay}
        </div>
        <section className="rounded-xl border border-slate-800 bg-slate-900/70 p-6 space-y-4" data-testid="preview-executive-summary">
          <h2 className="font-display text-xl font-bold">Executive Summary</h2>
          {(es.content || "").split("\n\n").filter(Boolean).map((p, i) => <p key={i} className="text-slate-300 leading-relaxed">{p}</p>)}
          {es.key_takeaways?.length > 0 && <ul className="list-disc pl-5 space-y-1 text-slate-300">{es.key_takeaways.map((t, i) => <li key={i}>{t}</li>)}</ul>}
        </section>
        <section className="rounded-xl border border-slate-800 bg-slate-900/70 p-6" data-testid="preview-market">
          <h2 className="font-display text-xl font-bold mb-4">Market size</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            {SECTIONS.find((s) => s.key === "market").fields.filter((f) => f.full).map((f) => (
              <div key={f.key} className="rounded-lg p-4 text-white" style={{ background: `var(--brand-${f.key})` }} data-testid={`preview-${f.key}`}>
                <p className="text-xs font-bold">{f.label}</p><p className="text-xs opacity-90">{f.full}</p>
                <p className="num text-2xl font-bold my-1">{money(d.market[f.key])}</p><p className="text-[11px] opacity-90 leading-snug">{f.define}</p>
              </div>
            ))}
          </div>
          {d.market.teaser && <p className="text-slate-300 leading-relaxed">{d.market.teaser}</p>}
        </section>
        {SECTIONS.filter((s) => s.key in d.teasers).map((s) => <Locked key={s.key} title={s.title} teaser={d.teasers[s.key]} invoice={d.invoice} />)}
        <div className="flex justify-center pb-10">{pay || <p className="text-sm text-slate-400">Your invoice is on its way. The full study unlocks once it's paid.</p>}</div>
      </main>
    </div>
  );
}
